import { useState } from "react";
import WelcomeName from "./WelcomeName";
import SignInSignOutButton from "./SignInSignOutButton";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { AuthenticatedTemplate } from "@azure/msal-react";
import { isDemoMode } from "../utils/PowerBIApiCall";
import { useIsAdmin } from "../hooks/useIsAdmin";

/** Official WM Partners icon — white version for dark backgrounds */
const WmpLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 1080 1080"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#fff"
      d="M855.9,164.2c-43.3,75.7-101.1,176.7-152.8,266.9h0l-98.6-172.2h-129.4l-98.6,172.2-98.6-172.2-13.2-23h-124.9l13.2,23,156.6,273.6h133.8l96.4-168.4,96.4,168.4h133.8c5.5-9.7,210.7-368.3,210.7-368.3h-124.8Z"
    />
    <path
      fill="#fff"
      d="M926.4,821.1l-156.3-273.1h-133.8l-96.4,168.4-96.4-168.4h-133.8c-22.6,39.5-210.5,367.8-210.5,367.8h124.9c45.9-80.4,101.2-176.9,152.5-266.5h0s98.3,171.8,98.3,171.8h129.9l98.3-171.8,98.3,171.8,13.2,23h124.9l-13.2-23Z"
    />
  </svg>
);

/** Desktop navigation link with active underline indicator */
const NavLink = ({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const isActive =
    location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <RouterLink
      to={to}
      className={`text-sm font-medium no-underline transition-colors relative py-1 ${
        isActive ? "text-white" : "text-white/60 hover:text-white"
      }`}
    >
      {children}
      {isActive && (
        <span className="absolute -bottom-[11px] left-0 right-0 h-[2px] bg-wmp-400 rounded-full" />
      )}
    </RouterLink>
  );
};

/** Mobile navigation link */
const MobileNavLink = ({
  to,
  children,
  onClick,
}: {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
}) => {
  const location = useLocation();
  const isActive =
    location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <RouterLink
      to={to}
      onClick={onClick}
      className={`block px-3 py-2 rounded-md text-sm font-medium no-underline transition-colors ${
        isActive
          ? "bg-white/10 text-white"
          : "text-white/60 hover:text-white hover:bg-white/[0.06]"
      }`}
    >
      {children}
    </RouterLink>
  );
};

const NavBar = () => {
  const demo = isDemoMode();
  const { isAdmin, isWmp } = useIsAdmin();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = (mobile = false) => {
    const LinkComp = mobile ? MobileNavLink : NavLink;
    const close = mobile ? () => setMobileOpen(false) : undefined;
    return (
      <>
        <LinkComp to="/dashboard" onClick={close}>
          Dashboard
        </LinkComp>
        {isWmp && (
          <LinkComp to="/fund" onClick={close}>
            Fund
          </LinkComp>
        )}
        <LinkComp to="/profile" onClick={close}>
          Profile
        </LinkComp>
        {isAdmin && (
          <LinkComp to="/admin" onClick={close}>
            Admin
          </LinkComp>
        )}
      </>
    );
  };

  return (
    <nav className="bg-navy-900 border-b border-white/[0.06] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Left — Logo + desktop nav links */}
          <div className="flex items-center gap-6">
            <RouterLink
              to="/"
              className="flex items-center gap-2.5 no-underline group"
            >
              <WmpLogo />
              <span className="text-white text-[15px] font-semibold tracking-tight group-hover:text-white/90 transition-colors">
                WM Partners
              </span>
            </RouterLink>

            {process.env.REACT_APP_VERSION && (
              <span
                className="hidden sm:inline text-white/25 text-[10px] font-mono -ml-3.5 mt-1"
                title={`Build ${process.env.REACT_APP_GIT_SHA} · ${process.env.REACT_APP_BUILD_DATE}`}
              >
                v{process.env.REACT_APP_VERSION}
              </span>
            )}

            <div className="hidden sm:flex items-center gap-5 ml-2 border-l border-white/10 pl-6">
              {demo ? (
                navLinks()
              ) : (
                <AuthenticatedTemplate>{navLinks()}</AuthenticatedTemplate>
              )}
            </div>
          </div>

          {/* Right — user controls + mobile hamburger */}
          <div className="flex items-center gap-3">
            {demo ? (
              <span className="text-amber-400/80 text-[10px] font-semibold px-2.5 py-1 bg-amber-400/10 border border-amber-400/20 rounded-full uppercase tracking-wider">
                Demo
              </span>
            ) : (
              <>
                <WelcomeName />
                <SignInSignOutButton />
              </>
            )}

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileOpen((o) => !o)}
              className="sm:hidden p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileOpen && (
        <div className="sm:hidden border-t border-white/[0.06]">
          <div className="px-4 py-3 space-y-1">
            {demo ? (
              navLinks(true)
            ) : (
              <AuthenticatedTemplate>{navLinks(true)}</AuthenticatedTemplate>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;
